import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PlatformLayout } from "./platform-layout";

interface PlatformCategoryPref {
  key: string;
  label: string;
  description: string | null;
  group: string;
  severity: string | null;
  emailEnabled: boolean;
}

export function NotificationSettingsContent() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ categories: PlatformCategoryPref[] }>({
    queryKey: ["/api/platform/notification-prefs"],
  });

  const mutation = useMutation({
    mutationFn: async (vars: { key: string; emailEnabled: boolean }) =>
      apiRequest("PUT", `/api/platform/notification-prefs/${vars.key}`, {
        emailEnabled: vars.emailEnabled,
      }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/notification-prefs"] });
      toast({
        title: vars.emailEnabled ? "Email enabled" : "Email disabled",
        description: vars.key,
      });
    },
    onError: () => {
      toast({ title: "Failed to update preference", variant: "destructive" });
    },
  });

  const grouped = (data?.categories ?? []).reduce<Record<string, PlatformCategoryPref[]>>((acc, c) => {
    (acc[c.group] ||= []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold" data-testid="text-page-title">
          Notification Settings
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Toggle email delivery for super-admin alert categories. Disabling a category
          still records the event in the platform audit log — it just stops the
          broadcast email. Defaults are ON.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        Object.entries(grouped).map(([group, cats]) => (
          <Card key={group} data-testid={`card-group-${group}`}>
            <CardHeader>
              <CardTitle className="capitalize">{group}</CardTitle>
              <CardDescription>{cats.length} categories</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {cats.map((c) => (
                <div
                  key={c.key}
                  className="flex items-start justify-between gap-4 border-b last:border-b-0 py-2"
                  data-testid={`row-category-${c.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.label}</span>
                      {c.severity && (
                        <Badge variant="outline" className="text-xs">
                          {c.severity}
                        </Badge>
                      )}
                      <code className="text-xs text-muted-foreground">{c.key}</code>
                    </div>
                    {c.description && (
                      <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
                    )}
                  </div>
                  <Switch
                    checked={c.emailEnabled}
                    disabled={mutation.isPending}
                    onCheckedChange={(checked) =>
                      mutation.mutate({ key: c.key, emailEnabled: checked })
                    }
                    data-testid={`switch-email-${c.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

export default function PlatformNotificationSettings() {
  return <PlatformLayout><NotificationSettingsContent /></PlatformLayout>;
}
